import { toFont } from 'chart.js/helpers'

import { formatNumber } from '../app/modules/utilities'

import { chartPalette, cssVar } from './palette'

const TOTALS_FONT = {
    weight: 600,
    size: 11,
    family: cssVar('--bs-font-monospace', 'monospace'),
}

// Chart.js plugin that renders each row's total as a flush right-aligned column
// pinned to the chart's right edge — the value column on the horizontal "ranked
// breakdown" bars (see BasePresenter.ranked_breakdown_bar_chart_js).
//
// This is used instead of datalabels plugin as it allows for more control over
// the positioning and styling totals column
export const TotalsAxisPlugin = {
    id: 'totalsAxis',

    beforeInit(chart) {
        TotalsAxisPlugin.beforeUpdate(chart)
    },

    beforeUpdate(chart) {
        const scales = chart.options.scales
        if (!scales) return

        const labels = totalsLabels(chart)

        scales.y2 ??= {
            type: 'category',
            position: 'right',
            offset: true, // center ticks on rows, matching the bar (index) axis
            grid: { display: false, drawTicks: false },
            border: { display: false },
            ticks: {
                crossAlign: 'far', // flush right-aligned column
                font: TOTALS_FONT,
            },
        }

        scales.y2.ticks.color = chartPalette().totalInk

        // The category labels on the left get the same ink as the totals column,
        // overriding the dimmer global tick color — only ranked bars opt into
        // this plugin, so other charts keep the default.
        scales.y ??= {}
        scales.y.ticks ??= {}
        scales.y.ticks.color = chartPalette().labelInk

        // Hidden for the empty "No Data" placeholder (charts_js_controller swaps in
        // a dataset with no values); flips back when a reload brings real data.
        scales.y2.display = hasAnyValue(chart.data.datasets)
        scales.y2.labels = labels

        // Keep the source column readable on narrow cards by capping the left axis.
        scales.y.afterFit = (scale) => {
            const chartWidth = chart.width
            if (!chartWidth) return

            const maxLeft = Math.floor(chartWidth * 0.45)
            if (scale.width > maxLeft) scale.width = maxLeft
        }

        // Guarantee room for long custom totals (e.g. "12 · avg 3.0 · p95 4.0").
        scales.y2.afterFit = (scale) => {
            const measured = measureLabelsWidth(chart, labels, TOTALS_FONT)
            if (measured > 0) scale.width = Math.max(scale.width, measured + 8)
        }
    },
}

function hasAnyValue(datasets) {
    return (datasets ?? []).some((ds) => (ds.data ?? []).some((v) => rawValue(v)))
}

// Per-row totals over the visible datasets, formatted with the datasets' shared
// formatter — the same summing/formatter rules as the datalabels formatter in
// charts_js_controller (which pies still use for on-slice labels).
function totalsLabels(chart) {
    if (chart.data.totalsLabels) return chart.data.totalsLabels

    const { labels = [], datasets = [] } = chart.data
    const totals = new Array(labels.length).fill(0)

    datasets.forEach((ds, idx) => {
        if (!chart.isDatasetVisible(idx)) return
        for (let i = 0; i < totals.length; i++) {
            totals[i] += rawValue(ds.data?.[i])
        }
    })

    // We need the same formatter for all datasets in order to format the summed totals
    let formatter
    const formatters = [...new Set(datasets.map((ds) => ds.formatter))]
    if (formatters.length > 1) {
        console.error('There are more than one formatter and cannot apply format: ', formatters)
    } else {
        formatter = formatters[0]
    }

    return totals.map((total) => formatNumber(total, formatter))
}

// Data points come in three shapes: raw number, {x} / {y} objects, or absent.
function rawValue(v) {
    if (v == null) return 0
    return typeof v === 'object' ? (v.x ?? v.y ?? 0) : +v || 0
}

function measureLabelsWidth(chart, labels, fontSpec) {
    const { ctx } = chart
    if (!ctx) return 0

    ctx.save()
    ctx.font = toFont(fontSpec).string
    const widths = labels.map((label) => ctx.measureText(String(label)).width)
    ctx.restore()
    return widths.length ? Math.max(...widths) : 0
}
